import { DOCUMENT } from '@angular/common';
import { provideHttpClient, withInterceptors, withXhr } from '@angular/common/http';
import {
  ApplicationConfig,
  ErrorHandler,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
} from '@angular/core';
import {
  provideRouter,
  withComponentInputBinding,
  withInMemoryScrolling,
  withRouterConfig,
  Router,
  NavigationStart,
  TitleStrategy,
} from '@angular/router';

import { provideTranslateService, TranslateLoader } from '@ngx-translate/core';
import { TRANSLATE_HTTP_LOADER_CONFIG } from '@ngx-translate/http-loader';
import { routes } from './app.routes';
import { errorInterceptor } from '@core/interceptors/error';
import { loaderInterceptor } from '@core/interceptors/loader';
import { tokenInterceptor } from '@core/interceptors/token';
import { prefetchEnglishWhenIdle } from '@core/startup/prefetch-english';
import { warmInitialRoute } from '@core/startup/initial-route-warmup';
import { AuthService } from '@core/services/auth.service';
import { ChartThemeService } from '@core/services/chart-theme.service';
import { initialLanguage } from '@core/services/language.service';
import { STORAGE_KEYS } from '@core/constants/storage-keys.constants';
import { migrateStorageKeys } from '@core/utils/storage-migration.utils';
import { readWebStorage } from '@core/utils/web-storage.utils';
import { ThemeService } from '@core/services/theme.service';
import { PrefetchingTranslateLoader } from '@core/services/translation-loader.service';
import { OverlayService } from '@shared/ui/services/overlay.service';
import { AppErrorHandler } from '@core/services/app-error-handler';
import { TranslatedTitleStrategy } from '@core/services/translated-title.strategy';
import { registerProductTours } from '@core/product-tours/product-tour-registry';
import { loadLeadManagementTours } from '@features/lead-management/utils/lead-management-tours.utils';
import { loadRealEstateTours } from '@features/real-estate/utils/real-estate-tours.utils';
import { registerApiErrorCodes } from '@core/utils/api-error-code-registry';
import {
  isRetargetingMessageCode,
  retargetingErrorTranslationKey,
} from '@features/lead-management/utils/retargeting-error-codes.utils';

// Before anything reads storage: values kept under the keys used before 19 Oct 2026 move to
// today's keys (and the user's data to this tab's sessionStorage).
migrateStorageKeys();
const startLanguage = initialLanguage(readWebStorage('local', STORAGE_KEYS.language));
// The product tours each area defines; their steps load only when a tour starts.
registerProductTours(loadLeadManagementTours);
registerProductTours(loadRealEstateTours);
// The API error codes an area gives its own texts in the global error toast.
registerApiErrorCodes({
  isMessageCode: isRetargetingMessageCode,
  translationKey: retargetingErrorTranslationKey,
});

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    // Angular's console output, then the error reporter (a no-op until a monitoring vendor is added).
    { provide: ErrorHandler, useClass: AppErrorHandler },
    provideHttpClient(
      withXhr(),
      withInterceptors([tokenInterceptor, loaderInterceptor, errorInterceptor])
    ),

    // Stamps `data-theme` on <html> before the first component renders. The inline script
    // in index.html has already applied it pre-paint, so nothing repaints here.
    provideAppInitializer(() => {
      inject(ThemeService);
      // Chart.js paints to a canvas and cannot read CSS variables.
      inject(ChartThemeService);
    }),
    provideAppInitializer(() => {
      void inject(AuthService).ensureSession();
    }),
    // The first page's chunks download while the session probe is still in flight.
    provideAppInitializer(() => {
      warmInitialRoute(routes, inject(DOCUMENT).location.pathname);
    }),
    provideAppInitializer(() => {
      const router = inject(Router);
      const overlays = inject(OverlayService);
      router.events.subscribe(event => {
        if (event instanceof NavigationStart) {
          overlays.closeAll();
        }
      });
    }),
    provideRouter(
      routes,
      withComponentInputBinding(),
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled' }),
      // Angular 22 makes every route inherit its ancestors' params, data and resolved data. Keep
      // the Angular 21 rule: a route inherits from its parent only when its own path is empty or
      // the parent has no component.
      withRouterConfig({ paramsInheritanceStrategy: 'emptyOnly' })
    ),
    // Tab: "<page> · Brooch CRM" from each route's translated title.
    { provide: TitleStrategy, useClass: TranslatedTitleStrategy },

    provideTranslateService({
      // English is the fallback for keys a language lacks. ar.json has every English key, so an
      // Arabic start loads only ar.json (see `prefetchEnglishWhenIdle`).
      fallbackLang: startLanguage === 'en' ? 'en' : undefined,
      lang: startLanguage,
      loader: { provide: TranslateLoader, useExisting: PrefetchingTranslateLoader },
    }),
    PrefetchingTranslateLoader,
    {
      provide: TRANSLATE_HTTP_LOADER_CONFIG,
      useValue: { prefix: '/assets/i18n/', suffix: '.json', useHttpBackend: true },
    },
    provideAppInitializer(() => prefetchEnglishWhenIdle()),
  ],
};
